import React, { useEffect, useMemo, useState } from 'react';
import AccountDialog from './AccountDialog';
import { NavRow } from './AccountParts';
import styles from './Account.module.css';
import { IconCamera, IconImage, IconSparkle, IconTrash } from './AccountIcons';
import { buildPresetAvatars } from '../../utils/presetAvatars';

/**
 * How you want to set the picture, asked once rather than spread across four
 * buttons under the avatar.
 */
export function AvatarSourceDialog({
  open,
  onClose,
  hasImage,
  canTakePhoto,
  onTakePhoto,
  onChooseFile,
  onChoosePreset,
  onRemove,
}) {
  return (
    <AccountDialog
      open={open}
      onClose={onClose}
      title={hasImage ? 'Change your picture' : 'Add a picture'}
    >
      <div className={styles.rowGroup}>
        {canTakePhoto ? (
          <NavRow
            icon={<IconCamera size={17} />}
            label="Take a photo"
            hint="Use your camera now"
            onClick={onTakePhoto}
          />
        ) : null}
        <NavRow
          icon={<IconImage size={17} />}
          label="Upload a photo"
          hint="Choose one from this device"
          onClick={onChooseFile}
        />
        <NavRow
          icon={<IconSparkle size={17} />}
          label="Pick a Velox avatar"
          hint="Ready-made, no photo needed"
          onClick={onChoosePreset}
        />
        {hasImage ? (
          <NavRow
            icon={<IconTrash size={17} />}
            label="Remove picture"
            hint="Go back to your initials"
            tone="danger"
            onClick={onRemove}
          />
        ) : null}
      </div>
    </AccountDialog>
  );
}

/**
 * The ready-made avatars. Generated from the signed-in name so the monograms
 * are already personal, and only while open so a closed dialog costs nothing.
 */
export function AvatarPresetDialog({ open, onClose, name, onSelect }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const presets = useMemo(() => (open ? buildPresetAvatars(name) : []), [open, name]);

  useEffect(() => {
    if (!open) {
      setBusyId(null);
      setError('');
    }
  }, [open]);

  const choose = async (preset) => {
    setError('');
    setBusyId(preset.id);
    try {
      await onSelect(preset.dataUrl);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not save that avatar. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AccountDialog open={open} onClose={onClose} title="Pick an avatar" lead="Tap one to use it.">
      <div className={styles.presetGrid}>
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={styles.presetTile}
            onClick={() => choose(preset)}
            disabled={busyId !== null}
            aria-label={preset.label}
            data-busy={busyId === preset.id ? 'true' : 'false'}
          >
            <img className={styles.presetImage} src={preset.dataUrl} alt="" />
          </button>
        ))}
      </div>

      {error ? (
        <p className={styles.formError} role="alert">
          {error}
        </p>
      ) : null}
    </AccountDialog>
  );
}
